import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import BaileyAvatar from '../../agent/bailey/BaileyAvatar';
import { getBaileyActivity } from '../../agent/bailey/activity';
import { useBaileyRun, publishRun, previewOf, getRun, useDismissedAt, setDismissedAt } from '../../agent/bailey/baileyRun';
import { openProposal, describeProposal, ITEM_CREATED_EVENT } from '../../agent/bailey/baileyBooking';
import { threadTitle } from '../../agent/bailey/format';
import BaileyReply from '../../agent/bailey/skills/BaileyReply';
import DraftWidget from '../../agent/bailey/skills/widgets/DraftWidget';
import { ThinkingWord } from '../../agent/bailey/thinking';
import { letteredAnswer } from '../../agent/bailey/skills/widgets/ChoicesWidget';
import { tidyWords } from '../../agent/bailey/skills/tidy';
import { campaignOfCard, campaignDraftsOf, draftCardHomes, showsDraftCardAt, messagesFromTranscript, draftsToOpen } from '../../agent/draftCards';
import { messageFor, markBriefed, notePath } from '../../agent/bailey/skills/context';
import { yesSaves, hasSavers, saveLatest, savedLine, isChangeIntent, hasEditors, editLatest, changedLine, screenFacts } from '../../agent/bailey/skills/saveBus';
import { stripContext, readyLine } from '../../agent/bailey/skills/protocol';
import { placeForPath, hrefFor } from '../../agent/bailey/skills/knowledge';
import { readMs } from '../../agent/bailey/thoughts';
import agentService from '../../../_services/agent.service';
import { hapticSuccess, hapticTick } from '../../../_utils/haptics';
import { BAILEY_INTRO_ENABLED, INTRO_EVENT, isIntroActive, isIntroDone } from './introStage';
import { usePet, setPet } from './petStore';
import { isPublicPath } from '../../template/publicPaths';
import { IDLE_ANTICS, isAntic, pickAntic } from '../../agent/bailey/idleAntics';
import { dust, playFx } from './anticFx';
import { resolveAppearance, cachedLook, rememberLook } from '../../agent/bailey/lookStore';
import useThoughts, { useIdeaRows } from './useThoughts';
import { IdeaIcon, ArrowRight, ArrowUp, Close, Tick } from './petIcons';
import './BaileyPet.css';
import { quotaRefusal, staffLockedCopy } from '../../pricing/usageRules';
import { bumpBaileyUsed, markBaileyOut } from '../../pricing/usageStore';
import { BaileyQuotaCard } from '../../pricing/BaileyQuota';
import { isOwnerHere } from '../../permissions/access';

// One icon for "take this to the Bailey tab", from the pill and from the chat
const OpenInIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

/**
 * Bailey the desk pet.
 *
 * Once the welcome tour is finished, Bailey stays on screen as a small pet
 * the owner can drag anywhere. Where he is left is remembered (per person,
 * across pages and browser tabs), and he can be hidden from the side menu
 * (BaileyPetToggle). Left alone, he finds things to do (idleAntics.js):
 * brushing his hair, sweeping up, sending emails, taking selfies, a nap, a
 * dance.
 *
 * Staging only, with the rest of the welcome. Business side only: never on
 * the client side (their bookings and account, booking pages, the
 * marketplace). Not on the Bailey page itself (the big Bailey is there) or
 * while the tour is running (it has its own).
 *
 * He can be ASKED from any screen: tapping him opens a small ask box with
 * ideas for the page. What he's doing then shows as one line beside him
 * ("Checking the diary…", then the answer's first line), and something to
 * look at (a post, an email) opens his chat, where it can be read in full
 * and replied to. The screen he was asked from goes with the question as a
 * path only; the server turns it into a page name from its own list.
 *
 * He also THINKS OUT LOUD now and then (useThoughts.js, thoughts.js): when
 * he has something specific to offer for the page, like tomorrow's free
 * times as a post, a thought bubble shows it, and a tap does it.
 *
 * One thing beside him at a time: his chat, the ask box, the line about
 * what he's doing, or a thought. Clicking anywhere else closes the chat and
 * the ask box (the ask box keeps what was typed).
 *
 * What he is doing is shared (baileyRun), so a reply running on the Bailey
 * page or in another tab shows here too.
 */

const RUN_KEY = 'beu:bailey-intro-run';
const tourRunning = () => {
  try { return isIntroActive() || !!sessionStorage.getItem(RUN_KEY); } catch (e) { return isIntroActive(); }
};
const isPhone = () => {
  try { return window.matchMedia('(max-width: 767px)').matches; } catch (e) { return false; }
};
const size = () => (isPhone() ? { w: 78, h: 86 } : { w: 120, h: 132 });
// Touch screens can't hover, so there his pill (new chat / open Bailey)
// waits for a tap on him instead of hovering into view.
const canHover = () => {
  try { return window.matchMedia('(hover: hover)').matches; } catch (e) { return true; }
};

// Room the pill needs below him. When he sits lower than that allows, it
// opens above him instead.
const PILL_ROOM = 56;
const SESSION_KEY = 'agentSessionId'; // the Bailey page's own key, so it opens this thread
const THREAD_KEY = 'beu:bailey-pet-thread';
// A finished answer's line waits this long to be read, then counts as seen
const STATUS_KEEP_MS = 30 * 60 * 1000;

// Someone typing anywhere (a page's field, or his own ask box): nothing of
// his opens by itself and takes their cursor
const typingNow = () => {
  const el = document.activeElement;
  return !!(el && el.matches && el.matches('input, textarea, select, [contenteditable=""], [contenteditable="true"]'));
};

// Keeps him fully on screen, whatever the window did since he was put down.
function clamp(x, y) {
  const { w, h } = size();
  return {
    x: Math.min(Math.max(4, x), window.innerWidth - w - 4),
    y: Math.min(Math.max(4, y), window.innerHeight - h - 4),
  };
}
const defaultSpot = () => {
  const { w, h } = size();
  return { x: window.innerWidth - w - 24, y: window.innerHeight - h - (isPhone() ? 96 : 96) };
};

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};

// How long he stays still before finding something to do: a short wait the
// first time, so the owner notices, then a longer one between antics.
const firstAnticWait = () => 6000 + Math.random() * 4000;
const anticWait = () => 14000 + Math.random() * 12000;

// A brand-new signup lands with ?setup=1 and meets Bailey in the welcome
// first, so he doesn't wait on the desk before it starts.
const welcomeComing = () => {
  try { return !isIntroDone() && new URLSearchParams(window.location.search).get('setup') === '1'; } catch (e) { return false; }
};

/**
 * Whether the pet belongs in this session at all: staging and a professional.
 * Not tied to the welcome being finished: accounts made before it existed
 * never see it, and would never get him or his switch in the side menu.
 */
export function usePetAvailable() {
  const professional = useSelector((s) => !!(s.user && s.user.userInfo && s.user.userInfo.hairdresser));
  const [coming, setComing] = useState(welcomeComing);
  const [running, setRunning] = useState(tourRunning);
  useEffect(() => {
    const check = () => { setComing(welcomeComing()); setRunning(tourRunning()); };
    window.addEventListener(INTRO_EVENT, check);
    const t = setInterval(check, 1500); // the run flag lives in sessionStorage, which has no event
    return () => { window.removeEventListener(INTRO_EVENT, check); clearInterval(t); };
  }, []);
  return { available: BAILEY_INTRO_ENABLED && professional && !coming, running };
}

export default function BaileyPet() {
  const { available, running } = usePetAvailable();
  const pet = usePet();
  const location = useLocation();
  // Where they are, so the Bailey tab knows where they came from
  useEffect(() => { notePath(location.pathname); }, [location.pathname]);
  const history = useHistory();
  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [mood, setMood] = useState('idle');
  // His look from this device straight away (no pop from the default look);
  // on a first visit he waits a moment for the saved one before appearing
  const [look, setLook] = useState(() => cachedLook());
  const [lookReady, setLookReady] = useState(() => !!cachedLook());
  const [canChat, setCanChat] = useState(false);
  // Bailey not switched on for this person yet (agent status "not-enabled",
  // e.g. a newly invited self-employed stylist): a tap offers the switch,
  // the same one the Bailey tab shows, instead of doing nothing
  const [optIn, setOptIn] = useState(false);
  // An employed team member the owner hasn't switched Bailey on for: a tap
  // says who to ask (staff-not-enabled), never nothing.
  const [locked, setLocked] = useState(null);
  const [optBusy, setOptBusy] = useState(false);
  const [optErr, setOptErr] = useState('');
  const [landed, setLanded] = useState(false); // mid-pratfall, after being put down
  const landTimers = useRef([]); // cleared if he is picked up again mid-pratfall
  const later = (fn, ms) => { landTimers.current.push(setTimeout(fn, ms)); };
  const cancelLanding = () => { landTimers.current.forEach(clearTimeout); landTimers.current = []; };
  useEffect(() => () => cancelLanding(), []);
  const anticTimers = useRef([]); // his current antic's effects and ending
  const cancelAntic = () => { anticTimers.current.forEach(clearTimeout); anticTimers.current = []; };
  useEffect(() => () => cancelAntic(), []);
  const recentAntics = useRef([]);
  const hadAntic = useRef(false);
  const petRef = useRef(null);
  const drag = useRef(null);
  const tilt = useRef(null);
  const latest = useRef(null); // where he is this instant, for saving on drop

  const run = useBaileyRun();
  const [asking, setAsking] = useState(false);
  const [pillOpen, setPillOpen] = useState(false); // touch: shown by tapping him
  const dockRef = useRef(null);
  const [draft, setDraft] = useState('');
  // When the line beside him was last closed (in any tab)
  const dismissedAt = useDismissedAt();
  const [celebrate, setCelebrate] = useState(false);
  const inputRef = useRef(null);
  const focusAsk = useRef(true); // the ask box takes the cursor, unless opened from a thought on a phone
  const abortRef = useRef(null);
  const busy = !!(run && run.phase === 'working');
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const bubble = run && (run.at || 0) > dismissedAt && (run.phase === 'working' || Date.now() - (run.at || 0) < STATUS_KEEP_MS) ? run : null;

  // Bailey's own pages: the tab, and the receptionist (where Bailey is already on screen).
  const onBaileyPage = /^\/(assistant|settings\/ai-assistant|receptionist)/.test(location.pathname);
  // Business side only. On the client side (their own bookings and account,
  // salon booking pages, the marketplace) he is never shown: the same pages
  // where the business side menu hides itself.
  const clientSide = isPublicPath(location.pathname);
  const show = available && !running && pet.visible && !onBaileyPage && !clientSide;

  // Where he was left, kept on screen.
  useEffect(() => {
    if (!show) return undefined;
    const place = () => {
      const saved = getSaved();
      setPos(clamp(saved.x, saved.y));
    };
    const getSaved = () => (pet.x != null && pet.y != null ? { x: pet.x, y: pet.y } : defaultSpot());
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [show, pet.x, pet.y]);

  // His saved look, and whether tapping him has a chat to open.
  useEffect(() => {
    if (!show) return undefined;
    let alive = true;
    const giveUp = setTimeout(() => { if (alive) setLookReady(true); }, 1500);
    agentService.status()
      .then((res) => {
        const d = (res && res.data && res.data.responseData) || {};
        if (!alive) return;
        const saved = resolveAppearance(d);
        if (saved) { setLook(saved); rememberLook(saved); }
        setCanChat(!!d.available);
        setOptIn(!d.available && d.reason === 'not-enabled');
        setLocked(!d.available && d.reason === 'staff-not-enabled' ? d : null);
        setLookReady(true);
      })
      .catch(() => { if (alive) setLookReady(true); });
    return () => { alive = false; clearTimeout(giveUp); };
  }, [show]);

  // A finished reply: a nod, then back to idle.
  useEffect(() => {
    if (!run || run.phase !== 'done') return undefined;
    setCelebrate(true);
    const t = setTimeout(() => setCelebrate(false), 1800);
    return () => clearTimeout(t);
  }, [run && run.phase, run && run.at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!asking) return;
    if (focusAsk.current && inputRef.current) inputRef.current.focus();
    focusAsk.current = true;
  }, [asking]);
  // Leaving him mid-reply must not leave a request running with nobody listening.
  useEffect(() => () => { if (abortRef.current) abortRef.current(); }, []);

  const openThread = useCallback((sessionId) => {
    history.push(sessionId ? `/assistant?session=${encodeURIComponent(sessionId)}` : '/assistant');
  }, [history]);

  // The pet's own little thread, so an answer can be read in full and
  // replied to without leaving the page. Kept per tab across pages.
  const [thread, setThreadState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(THREAD_KEY)) || { sessionId: null, messages: [] }; }
    catch (e) { return { sessionId: null, messages: [] }; }
  });
  const threadRef = useRef(thread);
  const setThread = useCallback((fn) => {
    setThreadState((t) => {
      const next = typeof fn === 'function' ? fn(t) : fn;
      threadRef.current = next;
      try { sessionStorage.setItem(THREAD_KEY, JSON.stringify({ ...next, messages: next.messages.slice(-30) })); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);
  const [expanded, setExpanded] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const chatBodyRef = useRef(null);
  const replyRef = useRef(null);
  const focusReply = useRef(false); // the chat takes the cursor only when they opened it
  useEffect(() => {
    const el = chatBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, expanded]);
  useEffect(() => {
    if (!expanded) return;
    // The ask box gives way to the chat (keeping what was typed)
    setAsking(false);
    if (focusReply.current && replyRef.current) replyRef.current.focus();
    focusReply.current = false;
  }, [expanded]);

  // Closing the chat while he's still working means "not now": when the
  // answer lands it waits as a line beside him instead of opening again.
  const closedDuringRun = useRef(false);
  const closeChat = useCallback(() => {
    setExpanded(false);
    if (busyRef.current) closedDuringRun.current = true;
  }, []);

  const patchLastReply = useCallback((fn) => setThread((t) => {
    const messages = t.messages.slice();
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'them') { messages[i] = fn(messages[i]); break; }
    }
    return { ...t, messages };
  }), [setThread]);

  // Fill in a draft card that carries only its id
  const loadDraft = useCallback((kind, id) => {
    const key = `${kind}:${id}`;
    const patch = (fields) => setThread((t) => ({
      ...t,
      messages: t.messages.map((m) => (m.cards
        ? { ...m, cards: m.cards.map((c) => (c.type === 'draft' && c.key === key ? { ...c, ...fields } : c)) }
        : m)),
    }));
    patch({ loading: true, error: undefined });
    agentService.draft(kind, id)
      .then((res) => {
        const payload = res && res.data && res.data.responseData;
        patch(payload ? { loading: false, draft: payload } : { loading: false, error: '' });
      })
      .catch(() => patch({ loading: false, error: '' }));
  }, [setThread]);

  /**
   * Send one message. `fresh` (the ask box) starts a new conversation; a
   * reply from the chat continues this one.
   */
  const send = useCallback((raw, fresh) => {
    // "B" answers the lettered question on screen with its words
    const text = (!fresh && letteredAnswer(raw)) || String(raw || '').trim();
    if (!text || busy) return;
    setDismissedAt(0);
    hapticTick();
    closedDuringRun.current = false;
    const sessionId = fresh ? null : threadRef.current.sessionId;
    setThread((t) => ({
      sessionId,
      messages: [...(fresh ? [] : t.messages), { role: 'me', text }, { role: 'them', text: '', streaming: true, activity: 'Thinking' }],
    }));
    // The reply comes into the open chat, so nobody misses it (closing it
    // while he works means "not now": closedDuringRun)
    if (!closedDuringRun.current) { focusReply.current = false; setExpanded(true); }
    let reply = '';
    let last = 0;
    publishRun({ phase: 'working', title: text, activity: 'Thinking', tool: null, preview: '', sessionId, source: 'pet' });
    // The page goes as a path (the server names it from its own list), and
    // their words go with the app's context: the page they're on and what's
    // on it, the brand, and all of Beu for a new conversation
    // "Yes" to what Bailey just showed saves it first (saveBus)
    // "Less formal" changes it in place; plans on screen go with the turn
    const saving = fresh ? Promise.resolve('')
      : yesSaves(text, ([...(threadRef.current.messages || [])].reverse().find(m => m.role === 'them') || {}).text) && hasSavers() ? saveLatest().then(savedLine).catch(() => '')
        : isChangeIntent(text) && hasEditors() ? editLatest(text).then(l => changedLine(l, text)).catch(() => '')
          : Promise.resolve('');
    saving.then((done) => {
    const out = messageFor(text, { surface: 'pet', pathname: location.pathname, sessionId, saved: [done, ...(fresh ? [] : screenFacts())].filter(Boolean).join('\n') });
    abortRef.current = agentService.chatStream({ sessionId, message: out.message, appContext: out.appContext, page: { path: location.pathname } }, (ev) => {
      switch (ev.type) {
        case 'session':
          if (ev.sessionId) {
            if (out.full) markBriefed(ev.sessionId);
            setThread((t) => ({ ...t, sessionId: ev.sessionId }));
            publishRun({ sessionId: ev.sessionId });
            try { sessionStorage.setItem(SESSION_KEY, ev.sessionId); } catch (e) { /* ignore */ }
          }
          break;
        case 'thinking':
          patchLastReply((m) => ({ ...m, activity: 'Thinking' }));
          publishRun({ activity: 'Thinking', tool: null });
          break;
        case 'tool_start':
          patchLastReply((m) => ({ ...m, activity: ev.label || 'Working on it' }));
          publishRun({ activity: ev.label || 'Working on it', tool: ev.tool || null });
          break;
        case 'text_delta': {
          reply += ev.text || '';
          patchLastReply((m) => ({ ...m, text: reply, activity: null }));
          const now = Date.now();
          // Bailey's words only: a block that's still arriving isn't shown as text
          if (now - last > 250) { last = now; publishRun({ activity: 'Writing', tool: null, preview: previewOf(tidyWords(reply)) }); }
          break;
        }
        case 'done': {
          abortRef.current = null;
          bumpBaileyUsed();
          if (out.full) markBriefed(ev.sessionId || sessionId);
          if (ev.sessionId) {
            setThread((t) => ({ ...t, sessionId: ev.sessionId }));
            try { sessionStorage.setItem(SESSION_KEY, ev.sessionId); } catch (e) { /* ignore */ }
          }
          const proposal = (ev.cards || []).find((c) => c && ['booking', 'event', 'leave'].includes(c.type));
          // What Bailey drafted (a campaign), shown in the chat, not described
          const drafts = (ev.cards || []).filter((c) => campaignOfCard(c));
          patchLastReply((m) => ({
            ...m, text: ev.content || reply, streaming: false, activity: null,
            proposal: proposal || null,
            ...(drafts.length ? { cards: drafts } : {}),
          }));
          if (proposal) {
            // Straight into the diary's own panel, filled in. The line beside
            // him says what is waiting, and the save is theirs.
            openProposal(proposal, history);
            publishRun({
              phase: 'done', activity: null, tool: null, pendingKind: proposal.type,
              ...(ev.sessionId ? { sessionId: ev.sessionId } : {}),
              preview: `Ready to save: ${describeProposal(proposal)}`,
            });
            break;
          }
          // Something to look at (a post, an email): open the chat to show
          // it, unless they closed the chat while he worked or are typing
          // (then the line beside him says it's ready)
          const madeDraft = drafts.find((c) => c.draft);
          const ready = readyLine(ev.content || reply) || (madeDraft ? `Draft ready: ${madeDraft.draft.title || 'take a look'}` : '');
          if (ready && !closedDuringRun.current && !typingNow()) setExpanded(true);
          publishRun({ phase: 'done', activity: null, tool: null, ...(ev.sessionId ? { sessionId: ev.sessionId } : {}), preview: ready || previewOf(tidyWords(ev.content || reply)) || 'Done.' });
          break;
        }
        case 'error': {
          abortRef.current = null;
          // Out of free questions: the upgrade card, not an error line.
          const quota = quotaRefusal(ev);
          if (quota) {
            markBaileyOut(quota);
            patchLastReply((m) => ({ ...m, text: '', streaming: false, activity: null, quota }));
            publishRun({ phase: 'done', activity: null, tool: null, preview: quota.code === 'bailey_staff_access' ? 'Bailey isn’t switched on for you.' : 'This month’s questions are used.' });
            break;
          }
          patchLastReply((m) => ({ ...m, text: ev.message || 'Bailey couldn’t answer just now.', streaming: false, activity: null, error: true }));
          publishRun({ phase: 'error', activity: null, tool: null, preview: ev.message || 'Bailey couldn’t answer just now.' });
          break;
        }
        default:
      }
    });
    });
  }, [busy, location.pathname, history, setThread, patchLastReply]);

  // The ask box: a new conversation.
  const ask = useCallback(() => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    setAsking(false);
    send(text, true);
  }, [draft, busy, send]);

  const sendReply = useCallback(() => {
    const text = replyDraft.trim();
    if (!text || busy) return;
    setReplyDraft('');
    send(text, false);
  }, [replyDraft, busy, send]);

  // Opening the chat on a reply that ran somewhere else (the Bailey page,
  // another tab): load that conversation so there is something to read.
  const expand = useCallback(async () => {
    setAsking(false);
    // The cursor goes to the reply box, except on a phone, where the
    // keyboard would cover what they opened it to read
    focusReply.current = !isPhone();
    setExpanded(true);
    const sid = run && run.sessionId;
    if (!sid || sid === threadRef.current.sessionId || busy) return;
    try {
      const res = await agentService.transcript(sid);
      const rows = ((res.data && res.data.responseData) || {}).rows || [];
      const messages = messagesFromTranscript(rows, stripContext).slice(-12);
      setThread({ sessionId: sid, messages });
      // A reopened conversation's drafts carry only their id: open them live
      draftsToOpen(messages).forEach(({ kind, id }) => loadDraft(kind, id));
    } catch (e) { /* the line beside him is still there */ }
  }, [run, busy, setThread, loadDraft]);

  // Clicking anywhere else, or Escape, closes the chat. Something a widget
  // opens over the page can keep it open with data-bailey-keep.
  useEffect(() => {
    if (!expanded) return undefined;
    const away = (e) => {
      const t = e.target;
      if (dockRef.current && dockRef.current.contains(t)) return;
      if (t && t.closest && t.closest('[data-bailey-keep]')) return;
      closeChat();
    };
    const esc = (e) => { if (e.key === 'Escape') closeChat(); };
    document.addEventListener('pointerdown', away, true);
    window.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      window.removeEventListener('keydown', esc);
    };
  }, [expanded, closeChat]);

  // The ask box closes the same way, keeping what was typed for next time.
  useEffect(() => {
    if (!asking) return undefined;
    const away = (e) => { if (dockRef.current && !dockRef.current.contains(e.target)) setAsking(false); };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [asking]);

  // What he got ready was saved in the panel: say so, then run over to it.
  const runOverRef = useRef(null);
  useEffect(() => {
    const SAID = { booking: 'Booked. It’s in the diary.', event: 'Added. It’s in the diary.', leave: 'Time off’s in the diary.' };
    const onCreated = () => {
      const r = getRun();
      if (!r || !r.pendingKind) return;
      publishRun({ phase: 'done', pendingKind: null, preview: SAID[r.pendingKind] || 'Saved.' });
      if (runOverRef.current) runOverRef.current();
    };
    window.addEventListener('diary:booking-created', onCreated);
    window.addEventListener(ITEM_CREATED_EVENT, onCreated);
    return () => {
      window.removeEventListener('diary:booking-created', onCreated);
      window.removeEventListener(ITEM_CREATED_EVENT, onCreated);
    };
  }, []);

  // The trip: find what just landed (the diary marks it .dv2-event--landed),
  // bring it into view, run to its side, cheer, then trot home. Dragging him
  // at any point cancels it; reduced motion skips the running.
  const trip = useRef(null);
  const [tripping, setTripping] = useState(false);
  const posRef = useRef(null);
  posRef.current = pos;
  const glide = (from, to, onDone) => {
    const dist = Math.hypot(to.x - from.x, to.y - from.y);
    const dur = Math.min(1400, Math.max(450, (dist / 700) * 1000));
    const t0 = performance.now();
    setMood(to.x < from.x ? 'run-left' : 'run-right');
    const step = (now) => {
      if (!trip.current) return;
      const k = Math.min(1, (now - t0) / dur);
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      setPos({ x: from.x + (to.x - from.x) * e, y: from.y + (to.y - from.y) * e });
      if (k < 1) trip.current.raf = requestAnimationFrame(step);
      else onDone();
    };
    trip.current.raf = requestAnimationFrame(step);
  };
  const endTrip = () => {
    if (trip.current) { cancelAnimationFrame(trip.current.raf); trip.current.timers.forEach(clearTimeout); }
    trip.current = null;
    setTripping(false);
  };
  runOverRef.current = () => {
    if (!posRef.current || drag.current) return;
    endTrip();
    trip.current = { raf: 0, timers: [] };
    const home = posRef.current;
    const wait = (ms) => new Promise((r) => { if (trip.current) trip.current.timers.push(setTimeout(r, ms)); });
    (async () => {
      let el = null;
      for (let i = 0; i < 40 && trip.current && !el; i += 1) {
        el = document.querySelector('.dv2-event--landed');
        if (!el) await wait(100);
      }
      if (!trip.current) return;
      if (!el || reducedMotion()) { setCelebrate(true); await wait(1800); setCelebrate(false); endTrip(); return; }
      setTripping(true);
      try { el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); } catch (e) { /* ignore */ }
      await wait(450);
      if (!trip.current) return;
      const r = el.getBoundingClientRect();
      const { w, h } = size();
      const right = r.right + 6 + w < window.innerWidth;
      const dest = clamp(right ? r.right + 6 : r.left - w - 6, r.top + r.height / 2 - h * 0.7);
      glide(posRef.current, dest, async () => {
        setMood('complete');
        await wait(2600);
        if (!trip.current) return;
        glide(posRef.current, home, () => { setMood('idle'); endTrip(); });
      });
    })();
  };
  useEffect(() => () => endTrip(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Thinking out loud: only while nothing of his is open and he isn't busy,
  // and a session starts only once he's between antics.
  const thoughtsReady = show && canChat && !busy && !expanded && !asking && !bubble && !dragging && !tripping && !landed && !celebrate && !pillOpen;
  const thoughts = useThoughts({ enabled: show && canChat && lookReady, ready: thoughtsReady, calm: mood === 'idle', path: location.pathname, dock: dockRef, compact: isPhone() });
  const thinking = !!thoughts.thought;
  // A new idea lands with an "aha", then he mulls it over.
  const thoughtKey = thinking ? `${thoughts.thought.id}:${thoughts.index}` : '';
  useEffect(() => {
    if (!thoughtKey) { setMood((m) => (m === 'ponder' || m === 'idea' ? 'idle' : m)); return undefined; }
    setMood((m) => (isAntic(m) ? m : 'idea'));
    const t = setTimeout(() => setMood((m) => (m === 'idea' ? 'ponder' : m)), 900);
    return () => clearTimeout(t);
  }, [thoughtKey]);
  const rows = useIdeaRows(asking && canChat, location.pathname);

  // Left alone (idle, not hovered, not being carried, not busy with a
  // question or a thought), he finds something to do. Each antic is a rig
  // state; its page effects are timed to the pose.
  const occupied = busy || asking || expanded || tripping || celebrate || pillOpen || thinking;
  useEffect(() => {
    if (!show || mood !== 'idle' || occupied || reducedMotion()) return undefined;
    let timer;
    const start = () => {
      // Nobody is watching a hidden tab; wait until they are.
      if (document.hidden) { timer = setTimeout(start, 5000); return; }
      hadAntic.current = true;
      const id = pickAntic(recentAntics.current);
      recentAntics.current = [id, ...recentAntics.current].slice(0, 3);
      const antic = IDLE_ANTICS[id];
      cancelAntic();
      setMood(id);
      antic.fx.forEach((fx) => {
        anticTimers.current.push(setTimeout(() => {
          if (!petRef.current) return;
          const r = petRef.current.getBoundingClientRect();
          playFx(fx.type, r.left + r.width * fx.x, r.top + r.height * fx.y, r.width / 120);
        }, fx.at * 1000));
      });
      anticTimers.current.push(setTimeout(() => setMood('idle'), antic.duration * 1000));
    };
    timer = setTimeout(start, hadAntic.current ? anticWait() : firstAnticWait());
    return () => clearTimeout(timer);
  }, [show, mood, occupied]);

  // Asked something, or off to the diary: the antic stops there.
  useEffect(() => {
    if (!occupied || !isAntic(mood)) return;
    cancelAntic();
    setMood('idle');
  }, [occupied, mood]);

  const onPointerDown = useCallback((e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Carrying him must not select text on the page underneath.
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* capture is a nicety */ }
    if (trip.current) endTrip();
    drag.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false, lastX: e.clientX };
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 5) return;
    if (!d.moved) { d.moved = true; cancelLanding(); cancelAntic(); setPillOpen(false); setDragging(true); setLanded(false); setMood('panic'); hapticTick(); }
    const next = clamp(d.ox + dx, d.oy + dy);
    latest.current = next;
    setPos(next);
    // A little swing in the direction he is being carried.
    if (tilt.current) tilt.current.style.setProperty('--swing', `${Math.max(-14, Math.min(14, (e.clientX - d.lastX) * 1.4))}deg`);
    d.lastX = e.clientX;
  }, []);

  const onPointerUp = useCallback((e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (tilt.current) tilt.current.style.setProperty('--swing', '0deg');
    if (d.moved) {
      setDragging(false);
      const at = latest.current || pos;
      setPet({ x: Math.round(at.x), y: Math.round(at.y) });
      // Put down: he lands on his bum in a puff of dust, sits there dazed,
      // gets up and dusts himself off (the rig's 'land' pose).
      const { w, h } = size();
      const floor = at.y + h * 0.9;
      const mid = at.x + w / 2;
      cancelLanding();
      setLanded(true);
      setMood('land');
      later(() => { dust(mid, floor, { count: 10, spread: true }); hapticTick(); }, 110);
      later(() => dust(mid - w * 0.22, at.y + h * 0.72, { count: 3, spread: false, size: 0.7 }), 1500);
      later(() => dust(mid + w * 0.22, at.y + h * 0.72, { count: 3, spread: false, size: 0.7 }), 1750);
      later(() => dust(mid, at.y + h * 0.78, { count: 3, spread: false, size: 0.6 }), 1950);
      later(() => { setLanded(false); setMood('idle'); }, 2700);
      return;
    }
    // A tap: his ask box when the account has a chat, otherwise a friendly
    // nod. A tap while his chat or ask box is open closes it. On a touch
    // screen the tap shows (or hides) his pill, the way hovering does on
    // desktop, and the pencil in it opens the ask box.
    cancelAntic();
    if (canChat && expanded) { setMood('idle'); closeChat(); return; }
    if (canChat && asking) { setMood('idle'); setAsking(false); return; }
    if (canChat && !canHover()) { setMood('idle'); setPillOpen((open) => !open); hapticTick(); return; }
    if (canChat) { setMood('idle'); setAsking(true); return; }
    if (optIn || locked) { setMood('idle'); hapticTick(); if (expanded) closeChat(); else { focusReply.current = false; setExpanded(true); } return; }
    setMood('complete');
    setTimeout(() => setMood('idle'), 1500);
  }, [pos, canChat, optIn, locked, expanded, asking, closeChat]);

  const switchOn = useCallback(async () => {
    setOptBusy(true);
    setOptErr('');
    try {
      const res = await agentService.setEnabled(true);
      const d = (res && res.data && res.data.responseData) || {};
      if (d.enabled !== true) throw new Error('not enabled');
      hapticSuccess();
      setOptIn(false);
      setCanChat(true);
      setExpanded(false);
      setAsking(true);
    } catch (e) {
      setOptErr('I couldn’t switch on just now. Try again in a moment.');
    } finally {
      setOptBusy(false);
    }
  }, []);

  // Tapping anywhere else puts the pill away again.
  useEffect(() => {
    if (!pillOpen) return undefined;
    const away = (e) => { if (dockRef.current && !dockRef.current.contains(e.target)) setPillOpen(false); };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [pillOpen]);

  // A cancelled touch only puts him down; it is never a tap.
  const onPointerCancel = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (tilt.current) tilt.current.style.setProperty('--swing', '0deg');
    const at = latest.current || pos;
    if (d.moved) { cancelAntic(); setDragging(false); setPet({ x: Math.round(at.x), y: Math.round(at.y) }); setMood('idle'); }
  };

  if (!show || !pos || !lookReady) return null;
  // A draft revised in a later turn shows once, on the latest turn
  const draftHomes = draftCardHomes(thread.messages);
  // What Bailey drafted, as the client gets it, one tap from finishing
  const draftsFor = (m, i) => (m.cards || []).filter((c) => showsDraftCardAt(draftHomes, c, i)).map((c) => (
    <DraftWidget
      key={c.key || c.draftId}
      draft={c.draft}
      loading={c.loading}
      error={c.error}
      revised={!!c.revised}
      compact
      go={(path, state) => { history.push(path, state); closeChat(); }}
      onRetry={() => loadDraft(c.kind, c.draftId)}
    />
  ));
  const { w, h } = size();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const here = placeForPath(location.pathname);
  const compact = isPhone();
  // Things beside him open towards the middle of the screen
  const onRight = pos.x + w / 2 > vw / 2;

  // One thing beside him at a time
  let surface = null;
  if (!dragging) {
    if (expanded) surface = 'chat';
    else if (asking && canChat) surface = 'composer';
    else if (bubble) surface = 'status';
    else if (thinking) surface = 'thought';
  }

  // What his body says: the run wins over hover moods, except while he is
  // being carried or picking himself up.
  let state = mood;
  let activity = 'none';
  if (!dragging && !landed && !tripping) {
    if (busy) { state = 'working'; activity = getBaileyActivity('working', run.tool); }
    else if (run && run.phase === 'error' && bubble) state = 'error';
    else if (celebrate) state = 'complete';
  }
  // Thinking, he looks towards his thought
  if (state === 'ponder' || state === 'idea') activity = onRight ? 'left' : 'right';

  // Acting on an idea: ask him, or open the place it names
  const act = (a) => {
    if (!a) return;
    if (a.ask) { send(a.ask, true); return; }
    if (a.path) { history.push(a.path); return; }
    const to = a.go ? hrefFor(a.go, a.params || {}) : null;
    if (to) history.push(to.path, to.state);
  };
  const runIdea = (row) => { hapticTick(); setAsking(false); setDraft(''); act(row.act); };
  const takeThought = () => {
    const t = thoughts.accept();
    if (t) { hapticTick(); act(t.act); }
  };

  // Sat near the bottom, with no room for his pill below: it opens above him.
  const controlsAbove = pos.y + h + PILL_ROOM > vh;
  // Lines and thoughts sit on the side towards the middle, never off screen.
  const edge = (width) => (onRight
    ? { right: 0, maxWidth: Math.min(width, pos.x + w - 8) }
    : { left: 0, maxWidth: Math.min(width, vw - pos.x - 8) });
  const statusBelow = pos.y < 96;
  // The ask box: below him when there's room, otherwise above.
  const ASK_W = Math.min(300, vw - 16);
  const askLeft = Math.min(Math.max(8 - pos.x, onRight ? w - ASK_W : 0), vw - 8 - pos.x - ASK_W);
  const roomUnder = vh - (pos.y + h);
  const composerAbove = roomUnder < 210 && pos.y > roomUnder;
  // A thought rises from his head, or sits beside it near the top of the screen.
  const THOUGHT_W = Math.min(236, vw - 16);
  const thoughtBeside = pos.y < 150;
  const thoughtStyle = thoughtBeside
    ? { width: THOUGHT_W, top: 0, ...(onRight ? { right: w + 12 } : { left: w + 12 }) }
    : { width: THOUGHT_W, bottom: 'calc(100% + 18px)', ...(onRight ? { right: w * 0.42 } : { left: w * 0.42 }) };
  const dots = thoughtBeside
    ? [{ size: 7, x: onRight ? 4 : w - 11, y: 22 }, { size: 11, x: onRight ? -8 : w - 3, y: 10 }]
    : [{ size: 7, x: onRight ? w * 0.4 : w * 0.6 - 7, y: -6 }, { size: 11, x: onRight ? w * 0.47 : w * 0.53 - 11, y: -19 }];
  // The chat: as tall as the room above him allows (or below, when he is
  // near the top), never off screen.
  const CHAT_W = Math.min(380, vw - 16);
  const chatLeft = Math.min(Math.max(8 - pos.x, (w - CHAT_W) / 2), vw - 8 - pos.x - CHAT_W);
  const roomAbove = pos.y - 16;
  const roomBelow = vh - (pos.y + h) - 16;
  const chatBelow = roomAbove < 260 && roomBelow > roomAbove;
  const chatMaxH = Math.max(220, Math.min(520, chatBelow ? roomBelow : roomAbove));

  const ideas = !draft.trim() && !busy && rows.length ? (
    <div className="bp-ideas" role="group" aria-label={here ? `Ideas for ${here.name}` : 'Ideas'}>
      {rows.map((r) => (
        <button key={r.id} type="button" className="bp-idea" onClick={() => runIdea(r)}>
          <IdeaIcon name={r.icon} />
          <span>{r.label}</span>
        </button>
      ))}
    </div>
  ) : null;

  return ReactDOM.createPortal(
    <div
      ref={dockRef}
      className={`bp-dock${dragging ? ' is-dragging' : ''}${surface === 'composer' ? ' is-asking' : ''}${controlsAbove ? ' controls-above' : ''}${expanded || asking || dragging ? ' is-raised' : ''}${onRight ? ' on-right' : ' on-left'}`}
      style={{ left: pos.x, top: pos.y, width: w }}
      // The whole dock, so moving down onto the pill keeps him happy. He is
      // pleased to see you, but finishes whatever antic he is in the middle
      // of, and keeps mulling over a thought.
      onMouseEnter={() => { if (!dragging && !landed && !expanded) setMood((m) => (isAntic(m) || m === 'ponder' || m === 'idea' ? m : 'excited')); }}
      onMouseLeave={() => { if (!dragging && !landed) setMood((m) => (isAntic(m) || m === 'ponder' || m === 'idea' ? m : 'idle')); }}
    >
      {surface === 'chat' && (
        <div
          className={`bp-chat${chatBelow ? ' is-below' : ''}`}
          style={{ left: chatLeft, width: CHAT_W, maxHeight: chatMaxH }}
          role="dialog"
          aria-label="Bailey"
        >
          <div className="bp-chat-head">
            <span className="bp-chat-title">Bailey</span>
            <button type="button" className="bp-chat-icon" onClick={() => openThread(thread.sessionId || (run && run.sessionId))} aria-label="Open in Bailey" title="Open in Bailey">
              <OpenInIcon />
            </button>
            <button type="button" className="bp-chat-icon" onClick={closeChat} aria-label="Close" title="Close"><Close size={15} /></button>
          </div>
          <div className="bp-chat-body" ref={chatBodyRef}>
            {!canChat && optIn && (
              <div className="bp-optin">
                <p className="bp-optin-title">Switch me on to chat</p>
                <p className="bp-optin-line">Ask me about your diary, clients and takings, and I’ll help with the jobs that take your time. I only see what you can already see in Beu, and anything sent to clients or that costs money waits for you.</p>
                {optErr && <p className="bp-optin-err" role="alert">{optErr}</p>}
                <button type="button" className="bp-optin-btn" onClick={switchOn} disabled={optBusy}>{optBusy ? 'Switching on…' : 'Switch on Bailey'}</button>
              </div>
            )}
            {!canChat && locked && (
              <div className="bp-optin">
                <p className="bp-optin-title">{staffLockedCopy(locked).title}</p>
                <p className="bp-optin-line">{staffLockedCopy(locked).text}</p>
                <p className="bp-optin-line">{staffLockedCopy(locked).note}</p>
              </div>
            )}
            {canChat && !thread.messages.length && <p className="bp-chat-empty">Ask about this page, or anything.</p>}
            {thread.messages.map((m, i) => (
              <div key={i} className={`bp-msg is-${m.role}${m.error ? ' is-error' : ''}${m.cards && m.cards.length ? ' has-cards' : ''}`}>
                {m.role === 'me'
                  ? m.text
                  : m.quota ? <BaileyQuotaCard quota={m.quota} isOwner={isOwnerHere()} />
                  : (m.text
                    ? (
                      <BaileyReply
                        text={m.text}
                        className="bp-msg-md"
                        compact
                        surface="pet"
                        say={i === thread.messages.length - 1 && !m.streaming && !busy ? (words) => send(words, false) : null}
                        onNavigate={closeChat}
                        drafts={campaignDraftsOf(m.cards)}
                        after={draftsFor(m, i)}
                      />
                    )
                    : (m.cards && m.cards.length) ? draftsFor(m, i) : <span className="bp-msg-activity"><span className="bp-dots" aria-hidden="true" /><ThinkingWord activity={m.activity} /></span>)}
                {m.proposal && (
                  <button type="button" className="bp-msg-proposal" onClick={() => openProposal(m.proposal, history)}>
                    Ready to save: {describeProposal(m.proposal)}
                  </button>
                )}
              </div>
            ))}
          </div>
          {canChat && <form className="bp-chat-reply" onSubmit={(e) => { e.preventDefault(); sendReply(); }}>
            <input
              ref={replyRef}
              className="bp-ask-input"
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
              placeholder={busy ? 'Bailey’s on it…' : 'Reply to Bailey'}
              maxLength={2000}
              aria-label="Reply to Bailey"
            />
            <button type="submit" className="bp-ask-send" disabled={!replyDraft.trim() || busy} aria-label="Send"><ArrowUp /></button>
          </form>}
        </div>
      )}

      {surface === 'status' && (
        <div
          className={`bp-status is-${bubble.phase}${statusBelow ? ' is-below' : ''}`}
          style={edge(300)}
          role="status"
          aria-live="polite"
        >
          <button
            type="button"
            className="bp-status-main"
            onClick={expand}
            title={`${threadTitle({ title: bubble.title }).replace(/…$/, '')}. Open to read and reply.`}
          >
            {bubble.phase === 'working' && <span className="bp-dots" aria-hidden="true" />}
            {bubble.phase === 'done' && <span className="bp-status-tick" aria-hidden="true"><Tick /></span>}
            <span className="bp-status-text">
              {bubble.phase === 'working' ? (bubble.preview || <ThinkingWord activity={bubble.activity} />) : (bubble.preview || 'Done.')}
            </span>
          </button>
          {bubble.phase !== 'working' && (
            <button type="button" className="bp-status-x" aria-label="Dismiss" onClick={() => setDismissedAt(Date.now())}><Close size={13} /></button>
          )}
        </div>
      )}

      {surface === 'thought' && !compact && (
        <>
          {dots.map((d, i) => (
            <span key={i} className={`bp-thought-dot is-${i + 1}`} aria-hidden="true" style={{ left: d.x, top: d.y, width: d.size, height: d.size }} />
          ))}
          <div
            className={`bp-thought${onRight ? ' opens-left' : ' opens-right'}${thoughtBeside ? ' is-beside' : ''}`}
            style={thoughtStyle}
            onMouseEnter={() => thoughts.hold(true)}
            onMouseLeave={() => thoughts.hold(false)}
            onFocus={() => thoughts.hold(true)}
            onBlur={() => thoughts.hold(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); thoughts.dismiss(); } }}
          >
            <button type="button" className="bp-thought-main" onClick={takeThought} aria-label={`Idea from Bailey: ${thoughts.thought.said} ${thoughts.thought.cta || ''}`}>
              <span key={thoughtKey} className="bp-thought-text">{thoughts.thought.said}</span>
              <span className="bp-thought-foot">
                <span className="bp-thought-count">{thoughts.total > 1 ? `${thoughts.index + 1} of ${thoughts.total}` : ''}</span>
                <span className="bp-thought-go">{thoughts.thought.cta || 'Do it'}<ArrowRight size={13} /></span>
              </span>
            </button>
            <button type="button" className="bp-thought-x" aria-label="Not now" title="Not now" onClick={thoughts.dismiss}><Close size={12} /></button>
            <span
              key={`p-${thoughtKey}`}
              className={`bp-thought-progress${thoughts.held ? ' is-held' : ''}`}
              style={{ animationDuration: `${readMs(thoughts.thought.said)}ms` }}
              aria-hidden="true"
            />
          </div>
        </>
      )}

      {surface === 'thought' && compact && (
        <button
          type="button"
          className={`bp-cloud${onRight ? ' opens-left' : ' opens-right'}`}
          onClick={() => { hapticTick(); thoughts.end(); focusAsk.current = false; setAsking(true); }}
          aria-label="Bailey has an idea. Open to see it."
        >
          <i /><i /><i />
        </button>
      )}

      {thoughts.quietLine && !surface && (
        <div className="bp-say" style={edge(260)} role="status">{thoughts.quietLine}</div>
      )}

      <div
        ref={petRef}
        className={`bp-pet${dragging ? ' is-dragging' : ''}${landed ? ' is-landed' : ''}`}
        style={{ width: w, height: h }}
        role="button"
        tabIndex={0}
        aria-label={canChat ? 'Bailey. Drag to move, or open to chat.' : optIn ? 'Bailey. Drag to move, or tap to switch Bailey on.' : locked ? 'Bailey. Not switched on for you yet: tap to see who to ask.' : 'Bailey. Drag to move.'}
        aria-expanded={canChat ? (canHover() ? asking || expanded : pillOpen) : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && (canChat || optIn || locked)) { e.preventDefault(); if (expanded) closeChat(); else if (canChat) setAsking((open) => !open); else setExpanded(true); } }}
      >
        <div className="bp-pet-swing" ref={tilt}>
          <div className="bp-pet-shake">
            <BaileyAvatar appearance={look} state={state} activity={activity} />
          </div>
        </div>
      </div>

      {surface === 'composer' && (
        <form
          className={`bp-composer${composerAbove ? ' is-above' : ''}`}
          style={{ left: askLeft, width: ASK_W }}
          onSubmit={(e) => { e.preventDefault(); ask(); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setAsking(false); setDraft(''); } }}
          // Focus moving to something else on the page closes it too
          onBlur={(e) => { if (e.relatedTarget && dockRef.current && !dockRef.current.contains(e.relatedTarget)) setAsking(false); }}
        >
          {composerAbove && ideas}
          <div className="bp-composer-row">
            <input
              ref={inputRef}
              className="bp-ask-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={busy ? 'Bailey’s on something. One moment.' : `Ask about ${here ? here.name : 'this page'}, or anything`}
              maxLength={2000}
              aria-label="Ask Bailey"
            />
            <button type="submit" className="bp-ask-send" disabled={!draft.trim() || busy} aria-label="Send"><ArrowUp /></button>
          </div>
          {!composerAbove && ideas}
        </form>
      )}

      {/* The pill, unless something else is beside him (a line about what
          he's doing may share his side only when the pill opens below him) */}
      {canChat && !dragging && (!surface || (surface === 'status' && !controlsAbove)) && (
        <div className={`bp-pill${pillOpen ? ' is-open' : ''}`}>
          {/* Setup's progress lives in the bell and the Bailey tab; here it read as a loading spinner */}
          <button type="button" className="bp-pill-btn" onClick={() => { setPillOpen(false); setExpanded(false); setAsking(true); }} aria-label="New chat" title="New chat">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4zM13.5 6.5l4 4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <span className="bp-pill-sep" />
          <button type="button" className="bp-pill-btn" onClick={() => { setPillOpen(false); openThread(run && run.sessionId); }} aria-label="Open in Bailey" title="Open in Bailey">
            <OpenInIcon />
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}
